// The state machine behind the `important` strip: which notice is showing, and for how long.
//
// Kept free of PIXI (like roster_speech_lane.ts, whose rules these are modelled on) so the timing
// can be unit-tested without a renderer; important_message.ts owns the drawing and just asks this
// what to show.
//
// WHY THERE IS A QUEUE AT ALL. The strip is one slot, and before this it was last-write-wins:
// `ImportantMessage.update` tore down the in-flight fade and drew the new line over it. Several
// ember beats land on the same wave boundary by construction, so specific sentences were being
// destroyed on a fixed timer:
//
//   * the bonecaller reveal asks for 6000ms and `VICTORY` lands 2500ms later (VICTORY_DELAY_MS),
//   * the rescue's "You hear a cry for help!" asks for 3000ms, same 2500ms wipe,
//   * and `WAVE n` is posted by AssaultDirector.onWaveLanded one statement BEFORE the encounter
//     callbacks it then invokes, so the wave-8 banner and "Eight armored skeletons are rising"
//     collide every time.
//
// The encounter's line is always the one the player has not seen before, and it was always the one
// at risk. So nothing is replaced any more: a notice waits its turn, and every notice gets at least
// its tier's floor on screen.
//
// WHY THERE ARE TIERS. Twelve ember call sites all passed `"important"`, which made a deadline and
// a receipt the same object in the same slot -- so "Provisions have been left by the fire." could
// delay "Stop the caller before the next assault." An alert outranks a notice in the queue and gets
// a longer floor and warmer colour; see TIER_FLOOR_MS and important_message.ts.

export type NoticeTier = "alert" | "notice"

export interface LaneNotice {
    text: string
    tier: NoticeTier
    /** The hold the sender asked for, if any. Clamped into [floor, MAX_HOLD_MS]. */
    durationMs?: number
    /**
     * The message this came from, carried through untouched and opaque to the lane.
     *
     * A notice that has to wait its turn is drawn some seconds after it arrived, and by then the
     * only thing that still knows its `displayCallback` and the rest of its fields is the message
     * itself -- so it rides along rather than being reconstructed from the three fields above.
     */
    source?: any
}

/** The shortest time a notice may hold the slot, so a queue can never flash one past the player. */
export const TIER_FLOOR_MS: Record<NoticeTier, number> = { alert: 2200, notice: 1300 }
/** The longest, so one generous `duration` cannot sit on a line that others are waiting behind. */
export const MAX_HOLD_MS = 6500
/**
 * How many notices may wait.
 *
 * A short cap on purpose: the strip reports what is happening NOW, and a wave-boundary burst that
 * backed up six deep would still be announcing wave 7 during wave 8. When the queue is full the
 * oldest NOTICE is dropped -- never an alert, and never in favour of one: a receipt is what should
 * be lost when too much is happening at once.
 */
export const MAX_QUEUE = 3
/**
 * A frame gap this long means the tab was not being drawn (a backgrounded tab is rAF-starved, and
 * its socket keeps delivering -- see the suspend/resume protocol). Everything collected meanwhile is
 * stale, so the lane is flushed rather than replayed as a burst at the player who was not watching.
 */
export const STALE_FRAME_MS = 1500
/**
 * The dark beat between one notice leaving and the next arriving.
 *
 * Long enough for the strip's ~400ms fade-out to be most of the way down before the next line fades
 * in, so a handover reads as one thing replacing another rather than as text changing in place --
 * which is what every collision looked like when the strip was last-write-wins. Only spent when
 * something is actually waiting: a lone notice appears the moment it is pushed.
 */
export const HANDOVER_MS = 220

const RANK: Record<NoticeTier, number> = { alert: 1, notice: 0 }

/** Reading time when the sender named no duration: the strip's own long-standing rule. */
const wordClock = (text: string) => Math.max(1500, text.split(" ").length * 300)

export const holdFor = (notice: LaneNotice): number =>
    Math.min(MAX_HOLD_MS, Math.max(TIER_FLOOR_MS[notice.tier], notice.durationMs || wordClock(notice.text)))

export class ImportantLane {
    private queue: LaneNotice[] = []
    private showing: LaneNotice | null = null
    private elapsedMs = 0
    // Time left in the gap between two notices; see HANDOVER_MS.
    private gapMs = 0
    // Raised whenever `showing` becomes a different notice (or nothing), so the renderer can be
    // driven off a change rather than re-drawing the same line every frame.
    private dirty = false

    /** What the strip should be showing, or null for an empty lane. */
    current = (): LaneNotice | null => this.showing
    queueLength = (): number => this.queue.length

    /** True once per change of `current()`; reading it clears it. */
    takeChange = (): boolean => {
        const changed = this.dirty
        this.dirty = false
        return changed
    }

    /**
     * Offer a notice.
     *
     * A repeat of the line already showing REFRESHES it instead of queueing behind itself: the same
     * text arriving twice is a duplicate (a resumed wave re-announcing itself), not two things to
     * read. A repeat of something already waiting is dropped for the same reason.
     */
    push = (notice: LaneNotice) => {
        if (!notice.text) return
        if (this.showing?.text === notice.text && this.showing.tier === notice.tier) {
            this.elapsedMs = 0
            return
        }
        if (this.queue.some(q => q.text === notice.text && q.tier === notice.tier)) return
        this.queue.push(notice)
        this.trim()
        // Nothing is showing and nothing is handing over, so take the slot on this call rather than
        // on the next frame: a lone notice should not wait for a tick to appear.
        if (!this.showing && this.gapMs <= 0) this.promote()
    }

    update = (dtMs: number) => {
        if (dtMs >= STALE_FRAME_MS) {
            this.clear()
            return
        }
        if (this.showing) {
            this.elapsedMs += dtMs
            const floor = TIER_FLOOR_MS[this.showing.tier]
            // Its own time is up; or it has been readable for long enough and somebody waiting
            // MATCHES OR BEATS its rank.
            //
            // That rank test is the whole point, and a bare "somebody is waiting" quietly undid the
            // fix: it yields at the floor to anything at all, so `VICTORY` arriving 2500ms into the
            // bonecaller reveal took the slot at the alert's 2200ms floor, and the reveal lost three
            // and a half of its six seconds to the one message it was being protected from. A notice
            // now waits out an alert in full. Equal rank still yields, which is what drains a burst
            // of wave banners rather than queueing them up behind each other's full holds.
            const waiting = this.bestWaiting()
            const yields = waiting !== undefined && RANK[waiting] >= RANK[this.showing.tier]
            if (this.elapsedMs >= holdFor(this.showing) || (this.elapsedMs >= floor && yields)) {
                this.showing = null
                this.elapsedMs = 0
                this.dirty = true
                this.gapMs = this.queue.length > 0 ? HANDOVER_MS : 0
            }
            return
        }
        if (this.gapMs > 0) {
            this.gapMs = Math.max(0, this.gapMs - dtMs)
            if (this.gapMs > 0) return
        }
        this.promote()
    }

    /** Drop everything, showing and waiting. Used on a room change and on a stale frame. */
    clear = () => {
        this.queue = []
        if (this.showing) this.dirty = true
        this.showing = null
        this.elapsedMs = 0
        this.gapMs = 0
    }

    /** Highest rank first, oldest first within a rank -- so an alert jumps a queue of notices. */
    private promote = () => {
        const pick = this.nextIndex()
        if (pick < 0) return
        this.showing = this.queue.splice(pick, 1)[0]
        this.elapsedMs = 0
        this.dirty = true
    }

    /** The tier of whatever would take the slot next, or undefined with nothing waiting. */
    private bestWaiting = (): NoticeTier | undefined => {
        const pick = this.nextIndex()
        return pick < 0 ? undefined : this.queue[pick].tier
    }

    private nextIndex = (): number => {
        if (this.queue.length < 1) return -1
        let pick = 0
        for (let i = 1; i < this.queue.length; i++) {
            if (RANK[this.queue[i].tier] > RANK[this.queue[pick].tier]) pick = i
        }
        return pick
    }

    private trim = () => {
        while (this.queue.length > MAX_QUEUE) {
            const oldestNotice = this.queue.findIndex(q => q.tier === "notice")
            // All alerts: drop the oldest of those rather than growing without bound.
            this.queue.splice(oldestNotice < 0 ? 0 : oldestNotice, 1)
        }
    }
}
